import { useEffect } from 'react'
import {
    FaBrain,
    FaChartBar,
    FaChartLine,
    FaChevronDown,
    FaComments,
    FaDatabase,
    FaFilePdf,
    FaFileSignature,
    FaFolderOpen,
    FaGlobe,
    FaHistory,
    FaHome,
    FaInbox,
    FaPalette,
    FaPaperPlane,
    FaSearch,
    FaShieldAlt,
    FaSyncAlt,
    FaTasks,
    FaUniversity,
    FaUpload,
    FaUsers,
    FaUsersCog,
    FaVial,
    FaLayerGroup,
    FaListAlt,
    FaSignature,
    FaEnvelope,
} from 'react-icons/fa'
import { FaRobot } from 'react-icons/fa6'
import type { IconType } from 'react-icons'
import { RiLinksFill } from 'react-icons/ri'
import { MdManageSearch } from 'react-icons/md'
import { MdManageAccounts } from 'react-icons/md'
import { IoIosPersonAdd } from 'react-icons/io'

import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuthUserData, useUpdateAuthUserData, useIsPcpReferralTeamLeader } from '../authUserProvider'
import { MhlReviewBadge } from '../components/views/militaryHearingLossView/shared/MhlReviewBadge'
import { MhlScopedNavItem, type MhlScopeRequirement } from '../components/views/militaryHearingLossView/shared/MhlScopedNavItem'
import styles from '../globals/sidebar.module.scss'

export const SidebarDisplay = (organisation: string) => {
    const authUser = useAuthUserData()
    const updateAuthUser = useUpdateAuthUserData()
    const { isLeader: isTeamLeader } = useIsPcpReferralTeamLeader()
    const navigate = useNavigate()
    const location = useLocation()

    const handleNavigation = (path: string) => {
        if (authUser?.sidebar) {
            updateAuthUser({ ...authUser, sidebar: false })
        }
        navigate(path)
    }

    const isActive = (path: string) => {
        return (
            location.pathname === path ||
            (path === '/srds-home' && location.pathname.startsWith('/srds/')) ||
            (path === '/viewthru-batch' && location.pathname.startsWith('/viewthru-batch/')) ||
            (path === '/comms' && location.pathname.startsWith('/comms')) ||
            (path === '/military-hearing-loss/leads' && location.pathname.startsWith('/military-hearing-loss/client/'))
        )
    }

    return (
        <ul className={styles.nav}>
            {organisation == 'Sentinel' && sentinelSidebar(handleNavigation, isActive)}
            {organisation == 'Sensible' && reEngagementSidebar(handleNavigation, isActive)}
            {organisation == 'ADL' && adlSidebar(handleNavigation, isActive)}
            {organisation == 'MR' && mrSidebar(handleNavigation, isActive)}
            {organisation == 'PCPMO26' && pcpmo26Sidebar(handleNavigation, isActive)}
            {organisation == 'FairClaim' && fairclaimSidebar(handleNavigation, isActive)}
            {organisation == 'SentinelMatters' && sentinelMattersSidebar(handleNavigation, isActive)}
            {organisation == 'PCP Referral' && pcpReferralSidebar(handleNavigation, isActive)}
            {organisation == 'PCP Referral Engagement' &&
                pcpReferralEngagementSidebar(handleNavigation, isActive, isTeamLeader)}
            {organisation == 'MBFCheck' && mbfcheckSidebar(handleNavigation, isActive)}
            {organisation == 'MFC' && mfcSidebar(handleNavigation, isActive)}
            {organisation == 'Vision Launch' && visionLaunchSidebar(handleNavigation, isActive)}
            {organisation == 'Score' && scoreSidebar(handleNavigation, isActive)}
            {organisation == 'Insurance Audit' && insuranceAuditSidebar(handleNavigation, isActive)}
            {organisation == 'Military Hearing Loss' && militaryHearingLossSidebar(handleNavigation, isActive)}
            {organisation == 'Military Hearing Loss Admin' && militaryHearingLossAdminSidebar(handleNavigation, isActive)}

            {/* {organisation == 'Gorilla' && gorillaSidebar(handleNavigation, isActive)}
            {organisation == 'Allegiant' && allegiantSidebar(handleNavigation, isActive)}
            {organisation == 'Panaco' && panacoSidebar(handleNavigation, isActive)} */}
        </ul>
    )
}

const sentinelSidebar = (handleNavigation: (path: string) => void, isActive: (path: string) => boolean) => {
    return (
        <>
            <li className={isActive('/home') ? styles.active : ''}>
                <a onClick={() => handleNavigation('/home')}>
                    <FaHome /> Dashboard
                </a>
            </li>
            {/* <li className={isA`ctive('/chatbot') ? styles.active : ''}>
                <a onClick={() => handleNavigation('/chatbot')}>
                    <FaRobot /> Chatbot
                </a>
            </li> */}
            <li className={isActive('/client-search') ? styles.active : ''}>
                <a onClick={() => handleNavigation('/client-search')}>
                    <FaSearch /> Client Search
                </a>
            </li>

            <details
                className={styles.sidebarDropdown}
                open={
                    isActive('/cases') ||
                    isActive('/clients') ||
                    isActive('/media-claims') ||
                    isActive('/manual-add-client') ||
                    isActive('/needs-sorting-clients') ||
                    isActive('/loa-tracker')
                }
            >
                <summary>
                    <span className={styles.summaryContent}>
                        <FaFolderOpen />
                        Sentinel Claims
                    </span>
                    <FaChevronDown className={styles.chevron} />
                </summary>
                <ul className={styles.nestedStats}>
                    <li className={isActive('/cases') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/cases')}>
                            <FaFolderOpen /> Cases
                        </a>
                    </li>
                    <li className={isActive('/clients') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/clients')}>
                            <FaUsers /> Clients
                        </a>
                    </li>
                    <li className={isActive('/media-claims') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/media-claims')}>
                            <FaUsers /> Media Claims
                        </a>
                    </li>
                    <li className={isActive('/manual-add-client') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/manual-add-client')}>
                            <IoIosPersonAdd /> Add Client
                        </a>
                    </li>
                    <li className={isActive('/needs-sorting-clients') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/needs-sorting-clients')}>
                            <FaInbox /> Sort Manual Clients
                        </a>
                    </li>
                    <li className={isActive('/loa-tracker') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/loa-tracker')}>
                            <FaListAlt /> LOA Tracker
                        </a>
                    </li>
                </ul>
            </details>

            <details className={styles.sidebarDropdown} open={isActive('/srds-home')}>
                <summary>
                    <span className={styles.summaryContent}>
                        <FaChartLine />
                        PCP Introducers
                    </span>
                    <FaChevronDown className={styles.chevron} />
                </summary>
                <ul className={styles.nestedStats}>
                    <li className={isActive('/srds-home') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/srds-home')}>
                            <FaDatabase /> SRDS
                        </a>
                    </li>
                    <li className={isActive('/sentinel-matters-home') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/sentinel-matters-home')}>
                            <FaChartLine /> SentinelMatters
                        </a>
                    </li>
                    <li className={isActive('/mfc-home') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/mfc-home')}>
                            <FaChartLine /> Evans Hughes
                        </a>
                    </li>
                    <li className={isActive('/fairclaim-home') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/fairclaim-home')}>
                            <FaChartLine /> Claim Delivery API
                        </a>
                    </li>
                    <li>
                        <details className={styles.sidebarDropdown}>
                            <summary>
                                <span className={styles.summaryContent}>
                                    <FaChartLine />
                                    Referral
                                </span>
                                <FaChevronDown className={styles.chevron} />
                            </summary>
                            <ul className={styles.nestedStats}>
                                <li className={isActive('/pcp-referral/overview') ? styles.active : ''}>
                                    <a onClick={() => handleNavigation('/pcp-referral/overview')}>
                                        <FaChartLine /> Overview
                                    </a>
                                </li>
                                <li className={isActive('/pcp-referral/engagement') ? styles.active : ''}>
                                    <a onClick={() => handleNavigation('/pcp-referral/engagement')}>
                                        <FaSearch /> Engagement
                                    </a>
                                </li>
                                <li className={isActive('/pcp-referral/add-client') ? styles.active : ''}>
                                    <a onClick={() => handleNavigation('/pcp-referral/add-client')}>
                                        <IoIosPersonAdd /> Add Client
                                    </a>
                                </li>
                                <li className={isActive('/pcp-referral/engagement-stats') ? styles.active : ''}>
                                    <a onClick={() => handleNavigation('/pcp-referral/engagement-stats')}>
                                        <FaChartLine /> Stats
                                    </a>
                                </li>
                                <li className={isActive('/pcp-referral/esign-history') ? styles.active : ''}>
                                    <a onClick={() => handleNavigation('/pcp-referral/esign-history')}>
                                        <FaHistory /> eSign History
                                    </a>
                                </li>
                                <li className={isActive('/pcp-referral/users') ? styles.active : ''}>
                                    <a onClick={() => handleNavigation('/pcp-referral/users')}>
                                        <MdManageAccounts /> Users
                                    </a>
                                </li>
                            </ul>
                        </details>
                    </li>
                    <li>
                        <details className={styles.sidebarDropdown}>
                            <summary>
                                <span className={styles.summaryContent}>
                                    <FaChartLine />
                                    Inactive
                                </span>
                                <FaChevronDown className={styles.chevron} />
                            </summary>
                            <ul className={styles.nestedStats}>
                                {/* <li className={isActive('/sentinel-introducer-stats') ? styles.active : ''}>
                                    <a onClick={() => handleNavigation('/sentinel-introducer-stats')}>
                                        <FaChartLine /> Introducer
                                    </a>
                                </li> */}
                                <li className={isActive('/sentinel-home') ? styles.active : ''}>
                                    <a onClick={() => handleNavigation('/sentinel-home')}>
                                        <FaChartLine /> Sentinel
                                    </a>
                                </li>
                                <li className={isActive('/adl-home') ? styles.active : ''}>
                                    <a onClick={() => handleNavigation('/adl-home')}>
                                        <FaChartLine /> ADL
                                    </a>
                                </li>
                                <li className={isActive('/pcpmo26-home') ? styles.active : ''}>
                                    <a onClick={() => handleNavigation('/pcpmo26-home')}>
                                        <FaChartLine /> PCPMO26
                                    </a>
                                </li>
                                <li className={isActive('/mbfcheck-home') ? styles.active : ''}>
                                    <a onClick={() => handleNavigation('/mbfcheck-home')}>
                                        <FaChartLine /> MBFCheck
                                    </a>
                                </li>
                                <li className={isActive('/mail-referral-home') ? styles.active : ''}>
                                    <a onClick={() => handleNavigation('/mail-referral-home')}>
                                        <FaChartLine /> Check Finance
                                    </a>
                                </li>
                                {/* <li className={isActive('/vision-launch-home') ? styles.active : ''}>
                                    <a onClick={() => handleNavigation('/vision-launch-home')}>
                                        <FaChartLine /> Vision Launch
                                    </a>
                                </li> */}

                                {/* <li className={isActive('/gorilla-home') ? styles.active : ''}>
                                    <a onClick={() => handleNavigation('/gorilla-home')}>
                                        <FaChartLine /> Gorilla
                                    </a>
                                </li> */}
                                {/* <li className={isActive('/allegiant-home') ? styles.active : ''}>
                                    <a onClick={() => handleNavigation('/allegiant-home')}>
                                        <FaChartLine /> Allegiant
                                    </a>
                                </li> */}
                                {/* <li className={isActive('/panaco-home') ? styles.active : ''}>
                                    <a onClick={() => handleNavigation('/panaco-home')}>
                                        <FaChartLine /> Panaco
                                    </a>
                                </li> */}
                            </ul>
                        </details>
                    </li>
                </ul>
            </details>

            <details
                className={styles.sidebarDropdown}
                open={
                    isActive('/fairclaim-api') ||
                    isActive('/fairclaim-api/optics') ||
                    isActive('/fairclaim-api/linked-funding') ||
                    isActive('/fairclaim-api/test-suite') ||
                    isActive('/fairclaim-api/keys') ||
                    isActive('/ffj-api') ||
                    isActive('/ffj-api/optics') ||
                    isActive('/ffj-api/linked-funding') ||
                    isActive('/ffj-api/test-suite') ||
                    isActive('/ffj-api/keys')
                }
            >
                <summary>
                    <span className={styles.summaryContent}>
                        <FaVial />
                        Introducer APIs
                    </span>
                    <FaChevronDown className={styles.chevron} />
                </summary>
                <ul className={styles.nestedStats}>
                    <li
                        className={
                            isActive('/fairclaim-api') ||
                            isActive('/fairclaim-api/optics') ||
                            isActive('/fairclaim-api/linked-funding') ||
                            isActive('/fairclaim-api/test-suite') ||
                            isActive('/fairclaim-api/keys')
                                ? styles.active
                                : ''
                        }
                    >
                        <a onClick={() => handleNavigation('/fairclaim-api')}>
                            <FaVial /> Claim Delivery API
                        </a>
                    </li>
                    <li
                        className={
                            isActive('/ffj-api') ||
                            isActive('/ffj-api/optics') ||
                            isActive('/ffj-api/linked-funding') ||
                            isActive('/ffj-api/test-suite') ||
                            isActive('/ffj-api/keys')
                                ? styles.active
                                : ''
                        }
                    >
                        <a onClick={() => handleNavigation('/ffj-api')}>
                            <FaVial /> FFJ API
                        </a>
                    </li>
                </ul>
            </details>

            <details
                className={styles.sidebarDropdown}
                open={isActive('/comms')}
            >
                <summary>
                    <span className={styles.summaryContent}>
                        <FaComments />
                        Comms Management
                    </span>
                    <FaChevronDown className={styles.chevron} />
                </summary>
                <ul className={styles.nestedStats}>
                    <li className={isActive('/comms/search') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/comms/search')}>
                            <FaSearch /> Search
                        </a>
                    </li>
                    <li className={isActive('/comms/mailboxes') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/comms/mailboxes')}>
                            <FaSyncAlt /> Mailboxes
                        </a>
                    </li>
                    <li className={isActive('/comms/pipeline') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/comms/pipeline')}>
                            <FaLayerGroup /> Pipeline
                        </a>
                    </li>
                    <li>
                        <details
                            className={styles.sidebarDropdown}
                            {...(isActive('/comms/loc-batches') || isActive('/comms/document-ingest') ? { open: true } : {})}
                        >
                            <summary>
                                <span className={styles.summaryContent}>
                                    <FaPaperPlane />
                                    Letters
                                </span>
                                <FaChevronDown className={styles.chevron} />
                            </summary>
                            <ul className={styles.nestedStats}>
                                <li className={isActive('/comms/loc-batches') ? styles.active : ''}>
                                    <a onClick={() => handleNavigation('/comms/loc-batches')}>
                                        <FaPaperPlane /> LOC Batches
                                    </a>
                                </li>
                                <li className={isActive('/comms/document-ingest') ? styles.active : ''}>
                                    <a onClick={() => handleNavigation('/comms/document-ingest')} title="Upload and file LOCs received in the post">
                                        <FaInbox /> Daily Post (incoming)
                                    </a>
                                </li>
                            </ul>
                        </details>
                    </li>
                    <li className={isActive('/comms/lenders') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/comms/lenders')}>
                            <FaUniversity /> Lenders
                        </a>
                    </li>
                    <li className={isActive('/comms/categories') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/comms/categories')}>
                            <FaTasks /> Categories & Rules
                        </a>
                    </li>
                    <li className={isActive('/comms/models') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/comms/models')}>
                            <FaRobot /> AI Models
                        </a>
                    </li>
                </ul>
            </details>

            {/* Funding Data Dropdown */}
            <details className={styles.sidebarDropdown}>
                <summary>
                    <span className={styles.summaryContent}>
                        <FaChartBar />
                        Funding Data
                    </span>
                    <FaChevronDown className={styles.chevron} />
                </summary>
                <ul className={styles.nestedStats}>
                    <li className={isActive('/collect-funding-clients') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/collect-funding-clients')}>
                            <FaChartBar />
                            Collect Funding Clients
                        </a>
                    </li>
                    <li className={isActive('/allocate-claims') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/allocate-claims')}>
                            <FaChartBar />
                            Allocate Claims
                        </a>
                    </li>
                    {/* Allocate Claims (Full) hidden from sidebar — route still reachable directly */}
                    <li className={isActive('/ineligible-funding-clients') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/ineligible-funding-clients')}>
                            <FaChartBar />
                            Failed Audit
                        </a>
                    </li>
                    <li className={isActive('/introducer-funding-gap') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/introducer-funding-gap')}>
                            <FaChartBar />
                            non-fundable
                        </a>
                    </li>
                    <li className={isActive('/analyse-schema') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/analyse-schema')}>
                            <FaChartBar />
                            Analyse Schema
                        </a>
                    </li>
                    <li className={isActive('/admin/approved-lenders') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/admin/approved-lenders')}>
                            <FaUniversity />
                            Approved Lenders
                        </a>
                    </li>
                </ul>
            </details>

            {/* Military Hearing Loss Portal */}
            <details className={styles.sidebarDropdown} open={MHL_NAV_ITEMS.some((item) => isActive(item.path))}>
                <summary>
                    <span className={styles.summaryContent}>
                        <FaUsers />
                        Military Hearing Loss
                    </span>
                    <FaChevronDown className={styles.chevron} />
                </summary>
                <ul className={styles.nestedStats}>{mhlNavItems(handleNavigation, isActive, true)}</ul>
            </details>

            {/* Irresponsible Lending hidden from sidebar — routes still reachable directly */}

            {/* <li className={isActive('/knowledge-base') ? styles.active : ''}>
                <a onClick={() => handleNavigation('/knowledge-base')}>
                    <FaBrain /> Knowledge Base
                </a>
            </li> */}

            <details className={styles.sidebarDropdown}>
                <summary>
                    <span className={styles.summaryContent}>
                        <FaChartLine /> Re-Engagement
                    </span>
                    <FaChevronDown className={styles.chevron} />
                </summary>
                <ul className={styles.nestedStats}>
                    <li className={isActive('/re-engagement-overview') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/re-engagement-overview')}>
                            <FaChartLine /> Overview
                        </a>
                    </li>
                    <li className={isActive('/re-engagement-search') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/re-engagement-search')}>
                            <FaChartLine /> Client Search
                        </a>
                    </li>
                </ul>
            </details>

            <details
                className={styles.sidebarDropdown}
                open={
                    isActive('/contracts') ||
                    isActive('/introducers-directory') ||
                    isActive('/cancellation-readiness') ||
                    isActive('/omnibus-claims') ||
                    isActive('/claim-valuations') ||
                    isActive('/report-links') ||
                    isActive('/viewthru-batch') ||
                    isActive('/admin/credit-scrapers') ||
                    isActive('/insurance-audit') ||
                    isActive('/signature-review') ||
                    isActive('/staff-email-signature')
                }
            >
                <summary>
                    <span className={styles.summaryContent}>
                        <FaTasks />
                        Sentinel Tools
                    </span>
                    <FaChevronDown className={styles.chevron} />
                </summary>
                <ul className={styles.nestedStats}>
                    <li className={isActive('/omnibus-claims') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/omnibus-claims')}>
                            <FaLayerGroup /> Omnibus Claims
                        </a>
                    </li>
                    <li className={isActive('/viewthru-batch') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/viewthru-batch')}>
                            <FaUpload /> ViewThru Batch
                        </a>
                    </li>
                    <li className={isActive('/contracts') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/contracts')}>
                            <FaFileSignature /> Contracts
                        </a>
                    </li>
                    <li className={isActive('/signature-review') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/signature-review')}>
                            <FaSignature /> Signature Review
                        </a>
                    </li>
                    <li className={isActive('/introducers-directory') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/introducers-directory')}>
                            <RiLinksFill /> Introducers Directory
                        </a>
                    </li>
                    <li className={isActive('/cancellation-readiness') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/cancellation-readiness')}>
                            <FaShieldAlt /> Cancellation Readiness
                        </a>
                    </li>
                    <li className={isActive('/insurance-audit') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/insurance-audit')}>
                            <FaShieldAlt /> Insurance Audit
                        </a>
                    </li>
                    <li className={isActive('/staff-email-signature') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/staff-email-signature')}>
                            <FaEnvelope /> Staff Email Signature
                        </a>
                    </li>
                    <li className={isActive('/admin/credit-scrapers') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/admin/credit-scrapers')}>
                            <FaSyncAlt /> Credit Scrapers
                        </a>
                    </li>
                    <li className={isActive('/claim-valuations') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/claim-valuations')}>
                            <FaChartBar /> Claim Valuations
                        </a>
                    </li>
                    <li className={isActive('/report-links') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/report-links')}>
                            <RiLinksFill /> Report Builder
                        </a>
                    </li>
                </ul>
            </details>

            {/* Admin Dropdown — Sentinel admins only. The underlying pages
                still gate themselves via `isAdminUser`, this just hides the
                links from non-admin users in the sidebar UI. */}
            <details
                className={styles.sidebarDropdown}
                open={
                    isActive('/users') ||
                    isActive('/admin/insurance-audit') ||
                    isActive('/admin/batch-visibility') ||
                    isActive('/admin/omnibus-comms-upload') ||
                    isActive('/admin/document-access') ||
                    isActive('/admin/style-guide')
                }
            >
                <summary>
                    <span className={styles.summaryContent}>
                        <FaUsersCog />
                        Admin
                    </span>
                    <FaChevronDown className={styles.chevron} />
                </summary>
                <ul className={styles.nestedStats}>
                    <li className={isActive('/users') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/users')}>
                            <FaUsersCog /> Users
                        </a>
                    </li>
                    <li className={isActive('/admin/insurance-audit') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/admin/insurance-audit')}>
                            <FaUsersCog /> Insurance Audit Admin
                        </a>
                    </li>
                    <li className={isActive('/admin/batch-visibility') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/admin/batch-visibility')}>
                            <FaLayerGroup /> Omnibus Batch Visibility
                        </a>
                    </li>
                    <li className={isActive('/admin/omnibus-comms-upload') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/admin/omnibus-comms-upload')}>
                            <FaEnvelope /> Omnibus Comms Upload
                        </a>
                    </li>
                    <li className={isActive('/admin/document-access') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/admin/document-access')}>
                            <FaFilePdf /> Bundle Access Log
                        </a>
                    </li>
                    <li className={isActive('/admin/style-guide') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/admin/style-guide')}>
                            <FaPalette /> Style Guide
                        </a>
                    </li>
                </ul>
            </details>
        </>
    )
}

const adlSidebar = (handleNavigation: (path: string) => void, isActive: (path: string) => boolean) => {
    return (
        <>
            <li className={isActive('/adl-home') ? styles.active : ''}>
                <a onClick={() => handleNavigation('/adl-home')}>
                    <FaChartLine /> ADL Stats
                </a>
            </li>
            <li className={isActive('/adl-il-stats') ? styles.active : ''}>
                <a onClick={() => handleNavigation('/adl-il-stats')}>
                    <FaChartLine /> ADL IL Stats
                </a>
            </li>
        </>
    )
}

const mrSidebar = (handleNavigation: (path: string) => void, isActive: (path: string) => boolean) => {
    return (
        <>
            <li className={isActive('/mail-referral-home') ? styles.active : ''}>
                <a onClick={() => handleNavigation('/mail-referral-home')}>
                    <FaChartLine /> Mail Stats
                </a>
            </li>
            <li className={isActive('/mail-il-stats') ? styles.active : ''}>
                <a onClick={() => handleNavigation('/mail-il-stats')}>
                    <FaChartLine /> Mail IL Stats
                </a>
            </li>
        </>
    )
}

const pcpmo26Sidebar = (handleNavigation: (path: string) => void, isActive: (path: string) => boolean) => {
    return (
        <>
            <li className={isActive('/pcpmo26-home') ? styles.active : ''}>
                <a onClick={() => handleNavigation('/pcpmo26-home')}>
                    <FaChartLine /> PCPMO26 Stats
                </a>
            </li>
        </>
    )
}

const fairclaimSidebar = (handleNavigation: (path: string) => void, isActive: (path: string) => boolean) => {
    return (
        <>
            <li className={isActive('/fairclaim-home') ? styles.active : ''}>
                <a onClick={() => handleNavigation('/fairclaim-home')}>
                    <FaChartLine /> Claim Delivery API Stats
                </a>
            </li>
        </>
    )
}

const sentinelMattersSidebar = (handleNavigation: (path: string) => void, isActive: (path: string) => boolean) => {
    return (
        <>
            <li className={isActive('/sentinel-matters-home') ? styles.active : ''}>
                <a onClick={() => handleNavigation('/sentinel-matters-home')}>
                    <FaChartLine /> SentinelMatters Stats
                </a>
            </li>
        </>
    )
}

const pcpReferralSidebar = (handleNavigation: (path: string) => void, isActive: (path: string) => boolean) => {
    return (
        <>
            <details className={styles.sidebarDropdown} open>
                <summary>
                    <span className={styles.summaryContent}>
                        <FaChartLine />
                        PCP Referral
                    </span>
                    <FaChevronDown className={styles.chevron} />
                </summary>
                <ul className={styles.nestedStats}>
                    <li className={isActive('/pcp-referral/overview') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/pcp-referral/overview')}>
                            <FaChartLine /> Overview
                        </a>
                    </li>
                    <li className={isActive('/pcp-referral/engagement') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/pcp-referral/engagement')}>
                            <FaSearch /> Engagement
                        </a>
                    </li>
                    <li className={isActive('/pcp-referral/add-client') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/pcp-referral/add-client')}>
                            <IoIosPersonAdd /> Add Client
                        </a>
                    </li>
                    <li className={isActive('/pcp-referral/engagement-stats') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/pcp-referral/engagement-stats')}>
                            <FaChartLine /> Stats
                        </a>
                    </li>
                    <li className={isActive('/pcp-referral/esign-history') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/pcp-referral/esign-history')}>
                            <FaHistory /> eSign History
                        </a>
                    </li>
                    <li className={isActive('/pcp-referral/users') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/pcp-referral/users')}>
                            <MdManageAccounts /> Users
                        </a>
                    </li>
                </ul>
            </details>
        </>
    )
}

const pcpReferralEngagementSidebar = (
    handleNavigation: (path: string) => void,
    isActive: (path: string) => boolean,
    isTeamLeader = false,
) => {
    return (
        <>
            <li className={isActive('/pcp-referral/engagement') ? styles.active : ''}>
                <a onClick={() => handleNavigation('/pcp-referral/engagement')}>
                    <FaSearch /> Engagement
                </a>
            </li>
            <li className={isActive('/pcp-referral/add-client') ? styles.active : ''}>
                <a onClick={() => handleNavigation('/pcp-referral/add-client')}>
                    <IoIosPersonAdd /> Add Client
                </a>
            </li>
            {isTeamLeader && (
                <li className={isActive('/pcp-referral/team-stats') ? styles.active : ''}>
                    <a onClick={() => handleNavigation('/pcp-referral/team-stats')}>
                        <FaChartLine /> My Team
                    </a>
                </li>
            )}
        </>
    )
}

const mbfcheckSidebar = (handleNavigation: (path: string) => void, isActive: (path: string) => boolean) => {
    return (
        <>
            <li className={isActive('/mbfcheck-home') ? styles.active : ''}>
                <a onClick={() => handleNavigation('/mbfcheck-home')}>
                    <FaChartLine /> MBFCheck Stats
                </a>
            </li>
        </>
    )
}

const mfcSidebar = (handleNavigation: (path: string) => void, isActive: (path: string) => boolean) => {
    return (
        <>
            <li className={isActive('/mfc-home') ? styles.active : ''}>
                <a onClick={() => handleNavigation('/mfc-home')}>
                    <FaChartLine /> Evans Hughes
                </a>
            </li>
        </>
    )
}

const panacoSidebar = (handleNavigation: (path: string) => void, isActive: (path: string) => boolean) => {
    return (
        <>
            <li className={isActive('/panaco-home') ? styles.active : ''}>
                <a onClick={() => handleNavigation('/panaco-home')}>
                    <FaChartLine /> Panaco Stats
                </a>
            </li>
        </>
    )
}

const gorillaSidebar = (handleNavigation: (path: string) => void, isActive: (path: string) => boolean) => {
    return (
        <>
            <li className={isActive('/gorilla-home') ? styles.active : ''}>
                <a onClick={() => handleNavigation('/gorilla-home')}>
                    <FaChartLine /> Gorilla Stats
                </a>
            </li>
        </>
    )
}

const reEngagementSidebar = (handleNavigation: (path: string) => void, isActive: (path: string) => boolean) => {
    return (
        <>
            <details className={styles.sidebarDropdown}>
                <summary>
                    <span className={styles.summaryContent}>
                        <FaChartLine /> Re-Engagement
                    </span>
                    <FaChevronDown className={styles.chevron} />
                </summary>
                <ul className={styles.nestedStats}>
                    <li className={isActive('/re-engagement-overview') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/re-engagement-overview')}>
                            <FaChartLine /> Overview
                        </a>
                    </li>
                    <li className={isActive('/re-engagement-search') ? styles.active : ''}>
                        <a onClick={() => handleNavigation('/re-engagement-search')}>
                            <FaChartLine /> Client Search
                        </a>
                    </li>
                </ul>
            </details>
        </>
    )
}

const visionLaunchSidebar = (handleNavigation: (path: string) => void, isActive: (path: string) => boolean) => {
    return (
        <>
            <li className={isActive('/vision-launch-home') ? styles.active : ''}>
                <a onClick={() => handleNavigation('/vision-launch-home')}>
                    <FaChartLine /> Vision Launch Stats
                </a>
            </li>
        </>
    )
}

const scoreSidebar = (handleNavigation: (path: string) => void, isActive: (path: string) => boolean) => {
    return (
        <>
            <li className={isActive('/allocate-claims') ? styles.active : ''}>
                <a onClick={() => handleNavigation('/allocate-claims')}>
                    <FaChartBar /> Allocate Claims
                </a>
            </li>
            {/* Allocate Claims (Full) hidden from sidebar — route still reachable directly */}
            <li className={isActive('/introducer-funding-gap') ? styles.active : ''}>
                <a onClick={() => handleNavigation('/introducer-funding-gap')}>
                    <FaChartBar /> Not in funding batches (introducers)
                </a>
            </li>
        </>
    )
}

const insuranceAuditSidebar = (handleNavigation: (path: string) => void, isActive: (path: string) => boolean) => {
    return (
        <>
            <li className={isActive('/insurance-audit') ? styles.active : ''}>
                <a onClick={() => handleNavigation('/insurance-audit')}>
                    <FaShieldAlt /> Audit Cases
                </a>
            </li>
        </>
    )
}

const allegiantSidebar = (handleNavigation: (path: string) => void, isActive: (path: string) => boolean) => {
    return (
        <>
            <li className={isActive('/allegiant-home') ? styles.active : ''}>
                <a onClick={() => handleNavigation('/allegiant-home')}>
                    <FaChartLine /> Allegiant Stats
                </a>
            </li>
        </>
    )
}

/**
 * Military Hearing Loss nav items, in display order. Admin-only pages are
 * flagged so the agent variant can drop them. Shared by the agent, admin and
 * Sentinel sidebars so order and labels never drift.
 */
const MHL_NAV_ITEMS: {
    path: string
    label: string
    Icon: IconType
    adminOnly?: boolean
    /** Shown to agents only when their server scope has this flag (team leaders). */
    requires?: MhlScopeRequirement
    badge?: boolean
}[] = [
    { path: '/military-hearing-loss/lead-tracker', label: 'Overview', Icon: FaChartLine, requires: 'canViewStats' },
    { path: '/military-hearing-loss/leads', label: 'Clients', Icon: FaSearch, badge: true },
    { path: '/military-hearing-loss/add-client', label: 'Add Client', Icon: FaInbox },
    { path: '/military-hearing-loss/admin', label: 'Agent Stats', Icon: FaChartBar, requires: 'canViewStats' },
    {
        path: '/military-hearing-loss/website-visitors',
        label: 'Website Visitors',
        Icon: FaGlobe,
        requires: 'canViewStats',
    },
    { path: '/military-hearing-loss/users', label: 'Agents', Icon: FaUsers, requires: 'isTeamLeader' },
]

const mhlNavItems = (handleNavigation: (path: string) => void, isActive: (path: string) => boolean, admin: boolean) =>
    MHL_NAV_ITEMS.filter((item) => admin || !item.adminOnly).map(({ path, label, Icon, badge, requires }) =>
        !admin && requires ? (
            <MhlScopedNavItem
                key={path}
                path={path}
                label={label}
                Icon={Icon}
                requires={requires}
                active={isActive(path)}
                className={styles.active}
                onNavigate={handleNavigation}
            />
        ) : (
            <li key={path} className={isActive(path) ? styles.active : ''}>
                <a onClick={() => handleNavigation(path)}>
                    <Icon /> {label} {badge && <MhlReviewBadge />}
                </a>
            </li>
        ),
    )

const militaryHearingLossSidebar = (handleNavigation: (path: string) => void, isActive: (path: string) => boolean) => (
    <>{mhlNavItems(handleNavigation, isActive, false)}</>
)

const militaryHearingLossAdminSidebar = (handleNavigation: (path: string) => void, isActive: (path: string) => boolean) => (
    <>{mhlNavItems(handleNavigation, isActive, true)}</>
)
